import React from 'react';
import PropTypes from 'prop-types';

import { Box, Button, Flex, Input, Select, Text } from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { sideBarRoutes } from 'utils/StaticData';
import { customStaticRanges } from 'utils/StaticData';
import { enGB } from 'date-fns/locale';

import { FaChevronDown } from 'react-icons/fa';
import { rentalStatusMap, paymentStatusMap } from 'utils/StaticData';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import { useDatePickerPosition } from 'hooks/useDatePickerPosition';

const OrderListFilter = ({
  search,
  setSearch,
  last4Search,
  setLast4Search,
  paymentStatus,
  setPaymentStatus,
  rentalStatus,
  setRentalStatus,
  handleSyncData,
  adminDetails,
  setPage,
  setShowCalendar,
  state,
  showCalendar,
  tempDateRange,
  setTempDateRange: _setTempDateRange,
  calendarFilterTitle,
  handleApply,
  handleClear,
  handleSelect,
  exportButton,
  onReset,
  // Return Time filter props
  showReturnCalendar,
  setShowReturnCalendar,
  tempReturnDateRange,
  setTempReturnDateRange: _setTempReturnDateRange,
  returnCalendarFilterTitle,
  handleReturnApply,
  handleReturnClear,
  handleReturnSelect,
  // Capture date filter props
  showCaptureCalendar,
  setShowCaptureCalendar,
  tempCaptureDateRange,
  setTempCaptureDateRange: _setTempCaptureDateRange,
  captureCalendarFilterTitle,
  handleCaptureApply,
  handleCaptureClear,
  handleCaptureSelect,
}) => {
  const { datePickerButtonRef, getCalendarPosition } = useDatePickerPosition();

  return (
    <>
      <Flex
        flexWrap="wrap"
        align="flex-end"
        mb={4}
        gap={{ base: '14px', md: '20px' }}
      >
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={search ? 'green.500' : 'gray.400'}
          >
            Search
          </Text>
          <CommonSearch
            search={search}
            setSearch={setSearch}
            setPage={setPage}
            placeholder="Merchant, Order ID or PaymentIntent"
          />
        </Box>
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={last4Search ? 'green.500' : 'gray.400'}
          >
            Last 4
          </Text>
          <Input
            placeholder="Enter last 4 digits..."
            value={last4Search}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,4}$/.test(value)) {
                setLast4Search(value);
              }
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
            maxLength={4}
          />
        </Box>
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={paymentStatus ? 'green.500' : 'gray.400'}
          >
            Payment Status
          </Text>
          <Select
            value={paymentStatus}
            onChange={(e) => {
              setPaymentStatus(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          >
            <option value="">All</option>
            {Object.entries(paymentStatusMap).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Select>
        </Box>
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={
              state?.rentalStatus || rentalStatus ? 'green.500' : 'gray.400'
            }
          >
            Rental Status
          </Text>
          <Select
            value={state?.rentalStatus ? state?.rentalStatus : rentalStatus}
            onChange={(e) => {
              setRentalStatus(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          >
            <option value="">All</option>
            {Object.entries(rentalStatusMap).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Select>
        </Box>

        {/* Date Filter */}

        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={
              calendarFilterTitle !== 'Select Date' ? 'green.500' : 'gray.400'
            }
          >
            Rental Time
          </Text>
          <Box
            position="relative"
            flexShrink="0"
            width={{ base: '100%', md: '200px' }}
          >
            <Button
              ref={datePickerButtonRef}
              py="10px"
              px="15"
              fontWeight="semibold"
              type="button"
              bg="white"
              border="1px solid"
              borderColor="reviveGray"
              color="#292D32"
              borderRadius="8px"
              onClick={() => setShowCalendar(!showCalendar)}
              justifyContent="space-between"
              width={{ base: '100%', md: '200px' }}
            >
              {calendarFilterTitle}{' '}
              <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
            </Button>
            {/* Date Range Picker Dropdown */}
            {showCalendar && (
              <>
                <Box
                  position="fixed"
                  bg="rgb(0 0 0 / 40%)"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  zIndex="999"
                  onClick={() => setShowCalendar(false)}
                ></Box>
                <Box
                  p={{ base: '20px', md: '32px' }}
                  mt="8px"
                  position="fixed"
                  {...getCalendarPosition()}
                  zIndex="1000"
                  bg="white"
                  boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                  borderRadius="20px"
                  className="custom-date-picker"
                  maxWidth={{ base: '95vw', md: '900px' }}
                  width={{ base: '95vw', md: 'auto' }}
                  maxHeight="90vh"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  <DateRangePicker
                    ranges={tempDateRange}
                    onChange={handleSelect}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                    locale={enGB}
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date()}
                    staticRanges={customStaticRanges}
                    inputRanges={[]}
                  />
                  <Flex justifyContent="right" gap="12px" marginTop="30px">
                    <Button
                      className="clear-btn"
                      border="1px solid"
                      borderColor="reviveGray"
                      borderRadius="10px"
                      onClick={handleClear}
                      _hover={{ bg: 'gray.50' }}
                    >
                      Clear
                    </Button>
                    <Button
                      className="apply-btn"
                      bg="reviveOrange"
                      color="white"
                      borderRadius="10px"
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                      onClick={handleApply}
                    >
                      Apply
                    </Button>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Return Time Filter */}

        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={
              returnCalendarFilterTitle !== 'Select Date'
                ? 'green.500'
                : 'gray.400'
            }
          >
            Return Time
          </Text>
          <Box
            position="relative"
            flexShrink="0"
            width={{ base: '100%', md: '200px' }}
          >
            <Button
              py="10px"
              px="15"
              fontWeight="semibold"
              type="button"
              bg="white"
              border="1px solid"
              borderColor="reviveGray"
              color="#292D32"
              borderRadius="8px"
              onClick={() => setShowReturnCalendar(!showReturnCalendar)}
              justifyContent="space-between"
              width={{ base: '100%', md: '200px' }}
            >
              {returnCalendarFilterTitle}{' '}
              <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
            </Button>
            {/* Date Range Picker Dropdown */}
            {showReturnCalendar && (
              <>
                <Box
                  position="fixed"
                  bg="rgb(0 0 0 / 40%)"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  zIndex="999"
                  onClick={() => setShowReturnCalendar(false)}
                ></Box>
                <Box
                  p={{ base: '20px', md: '32px' }}
                  mt="8px"
                  position="fixed"
                  {...getCalendarPosition()}
                  zIndex="1000"
                  bg="white"
                  boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                  borderRadius="20px"
                  className="custom-date-picker"
                  maxWidth={{ base: '95vw', md: '900px' }}
                  width={{ base: '95vw', md: 'auto' }}
                  maxHeight="90vh"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  <DateRangePicker
                    ranges={tempReturnDateRange}
                    onChange={handleReturnSelect}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                    locale={enGB}
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date()}
                    staticRanges={customStaticRanges}
                    inputRanges={[]}
                  />
                  <Flex justifyContent="right" gap="12px" marginTop="30px">
                    <Button
                      className="clear-btn"
                      border="1px solid"
                      borderColor="reviveGray"
                      borderRadius="10px"
                      onClick={handleReturnClear}
                      _hover={{ bg: 'gray.50' }}
                    >
                      Clear
                    </Button>
                    <Button
                      className="apply-btn"
                      bg="reviveOrange"
                      color="white"
                      borderRadius="10px"
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                      onClick={handleReturnApply}
                    >
                      Apply
                    </Button>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* End Return Time filter  */}

        {/* Capture Date Filter */}

        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={
              captureCalendarFilterTitle !== 'Select Date'
                ? 'green.500'
                : 'gray.400'
            }
          >
            Capture Date
          </Text>
          <Box
            position="relative"
            flexShrink="0"
            width={{ base: '100%', md: '200px' }}
          >
            <Button
              py="10px"
              px="15"
              fontWeight="semibold"
              type="button"
              bg="white"
              border="1px solid"
              borderColor="reviveGray"
              color="#292D32"
              borderRadius="8px"
              onClick={() => setShowCaptureCalendar(!showCaptureCalendar)}
              justifyContent="space-between"
              width={{ base: '100%', md: '200px' }}
            >
              {captureCalendarFilterTitle}{' '}
              <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
            </Button>
            {/* Date Range Picker Dropdown */}
            {showCaptureCalendar && (
              <>
                <Box
                  position="fixed"
                  bg="rgb(0 0 0 / 40%)"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  zIndex="999"
                  onClick={() => setShowCaptureCalendar(false)}
                ></Box>
                <Box
                  p={{ base: '20px', md: '32px' }}
                  mt="8px"
                  position="fixed"
                  {...getCalendarPosition()}
                  zIndex="1000"
                  bg="white"
                  boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                  borderRadius="20px"
                  className="custom-date-picker"
                  maxWidth={{ base: '95vw', md: '900px' }}
                  width={{ base: '95vw', md: 'auto' }}
                  maxHeight="90vh"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  <DateRangePicker
                    ranges={tempCaptureDateRange}
                    onChange={handleCaptureSelect}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                    locale={enGB}
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date()}
                    staticRanges={customStaticRanges}
                    inputRanges={[]}
                  />
                  <Flex justifyContent="right" gap="12px" marginTop="30px">
                    <Button
                      className="clear-btn"
                      border="1px solid"
                      borderColor="reviveGray"
                      borderRadius="10px"
                      onClick={handleCaptureClear}
                      _hover={{ bg: 'gray.50' }}
                    >
                      Clear
                    </Button>
                    <Button
                      className="apply-btn"
                      bg="reviveOrange"
                      color="white"
                      borderRadius="10px"
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                      onClick={handleCaptureApply}
                    >
                      Apply
                    </Button>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* End Capture Date filter  */}

        <Button onClick={onReset}>Reset</Button>

        {(adminDetails?.role === sideBarRoutes?.Admin ||
          adminDetails?.role === sideBarRoutes?.Dealer) &&
          exportButton}

        {adminDetails?.role === sideBarRoutes?.Admin && (
          <Button
            backgroundColor={'red.500'}
            color="white"
            onClick={() => handleSyncData()}
          >
            Sync Data
          </Button>
        )}
      </Flex>
    </>
  );
};
OrderListFilter.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func,
  last4Search: PropTypes.string,
  setLast4Search: PropTypes.func,
  paymentStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setPaymentStatus: PropTypes.func,
  rentalStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setRentalStatus: PropTypes.func,
  handleSyncData: PropTypes.func,
  adminDetails: PropTypes.object,
  setPage: PropTypes.func,
  setShowCalendar: PropTypes.func,
  state: PropTypes.object,
  showCalendar: PropTypes.bool,
  tempDateRange: PropTypes.array,
  setTempDateRange: PropTypes.func,
  calendarFilterTitle: PropTypes.string,
  handleApply: PropTypes.func,
  handleClear: PropTypes.func,
  handleSelect: PropTypes.func,
  exportButton: PropTypes.node,
  onReset: PropTypes.func,
  // Return Time filter props
  showReturnCalendar: PropTypes.bool,
  setShowReturnCalendar: PropTypes.func,
  tempReturnDateRange: PropTypes.array,
  setTempReturnDateRange: PropTypes.func,
  returnCalendarFilterTitle: PropTypes.string,
  handleReturnApply: PropTypes.func,
  handleReturnClear: PropTypes.func,
  handleReturnSelect: PropTypes.func,
  // Capture date filter props
  showCaptureCalendar: PropTypes.bool,
  setShowCaptureCalendar: PropTypes.func,
  tempCaptureDateRange: PropTypes.array,
  setTempCaptureDateRange: PropTypes.func,
  captureCalendarFilterTitle: PropTypes.string,
  handleCaptureApply: PropTypes.func,
  handleCaptureClear: PropTypes.func,
  handleCaptureSelect: PropTypes.func,
};

export default OrderListFilter;
